import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import "./Dashboard.css";

function Dashboard() {
    useEffect(() => {
        document.body.classList.add('dashboard-page');
        return () => {
            document.body.classList.remove('dashboard-page');
        };
    }, []);

    const { currentUser } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [newTask, setNewTask] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Get time of day for greeting
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        return "Good evening";
    };

    // Fetch Google Tasks when component mounts
    useEffect(() => {
        fetchGoogleTasks();
    }, []);

    const fetchGoogleTasks = async () => {
        setIsLoading(true);
        try {
            // This would be your API call to fetch Google Tasks
            // For demonstration, we'll simulate this with a timeout and sample data

            // In a real implementation, this would be:
            // const response = await fetch('https://your-backend/api/google-tasks', {
            //    headers: { Authorization: `Bearer ${currentUser.token}` }
            // });
            // const data = await response.json();

            setTimeout(() => {
                const sampleTasks = [
                    { id: 'task1', title: "Follow up with Zulhilmi", completed: false },
                    { id: 'task2', title: "Prepare presentation", completed: true },
                    { id: 'task3', title: "Schedule property showing", completed: false },
                    { id: 'task4', title: "Submit paperwork to HDB", completed: false },
                    { id: 'task5', title: "Dinner meeting with other Agents", completed: false }
                ];
                setTasks(sampleTasks);
                setIsLoading(false);
            }, 1000);
        } catch (err) {
            console.error("Error fetching Google Tasks:", err);
            setError("Failed to load tasks. Please try again later.");
            setIsLoading(false);
        }
    };

    const toggleCompleteTask = async (id) => {
        try {
            // Find the task
            const task = tasks.find(t => t.id === id);
            if (!task) return;

            // Optimistically update UI
            const updatedTasks = tasks.map(t =>
                t.id === id ? { ...t, completed: !t.completed } : t
            );
            setTasks(updatedTasks);

            // In a real implementation, update the task in Google Tasks API
            // Example:
            // await fetch(`https://your-backend/api/google-tasks/${id}`, {
            //    method: 'PATCH',
            //    headers: { 
            //      'Content-Type': 'application/json', 
            //      Authorization: `Bearer ${currentUser.token}`
            //    },
            //    body: JSON.stringify({ completed: !task.completed })
            // });

            // If API call fails, we'd revert the change
            // But we're simulating success here
        } catch (err) {
            console.error("Error updating task status:", err);
            // Revert the change if there was an error
            setTasks(tasks);
            alert("Failed to update task. Please try again.");
        }
    };

    const addTask = async (e) => {
        e.preventDefault();
        if (!newTask.trim()) return;

        // Define taskData outside the try block so it's accessible in the catch block
        const taskData = {
            id: `temp-${Date.now()}`, // Temporary ID until server responds
            title: newTask,
            completed: false
        };

        try {
            // Optimistically add to UI
            setTasks([...tasks, taskData]);
            setNewTask("");

            // In a real implementation, send to Google Tasks API
            // Example:
            // const response = await fetch('https://your-backend/api/google-tasks', {
            //    method: 'POST',
            //    headers: { 
            //      'Content-Type': 'application/json', 
            //      Authorization: `Bearer ${currentUser.token}`
            //    },
            //    body: JSON.stringify({ title: newTask })
            // });
            // const data = await response.json();

            // Then update with the real task ID from the response
            // setTasks(tasks => tasks.map(t => t.id === taskData.id ? { ...t, id: data.id } : t));

        } catch (err) {
            console.error("Error adding task:", err);
            // Remove the task if there was an error - now taskData is accessible here
            setTasks(tasks => tasks.filter(t => t.id !== taskData.id));
            alert("Failed to add task. Please try again.");
        }
    };

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h1>{getGreeting()}, {currentUser?.name || "Agent"}</h1>
                <p className="dashboard-date">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>

            <div className="dashboard-content">
                <div className="dashboard-calendar">
                    <div className="section-header">
                        <h2>Your Schedule</h2>
                        <button className="section-action">+ Add Event</button>
                    </div>

                    <div className="calendar-placeholder">
                        <iframe
                            title="Google Calendar"
                            src="about:blank"
                            className="google-calendar-frame"
                        />
                        <div className="calendar-overlay">
                            <div className="calendar-message">
                                <div className="calendar-icon">📅</div>
                                <h3>Google Calendar Integration</h3>
                                <p>Calendar integration will be available soon.</p>
                            </div>
                        </div>
                    </div>

                    <div className="upcoming-events">
                        <h3>Today's Events</h3>
                        <ul className="event-list">
                            <li className="event">
                                <div className="event-time">9:00 AM</div>
                                <div className="event-details">
                                    <div className="event-title">Team Meeting</div>
                                    <div className="event-location">Conference Room B</div>
                                </div>
                            </li>
                            <li className="event">
                                <div className="event-time">11:30 AM</div>
                                <div className="event-details">
                                    <div className="event-title">Property Showing</div>
                                    <div className="event-location">123 Maple Street</div>
                                </div>
                            </li>
                            <li className="event">
                                <div className="event-time">2:00 PM</div>
                                <div className="event-details">
                                    <div className="event-title">Client Consultation</div>
                                    <div className="event-location">Office</div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="dashboard-todo">
                    <div className="section-header">
                        <h2>Google Tasks</h2>
                        <button className="section-action" onClick={fetchGoogleTasks} disabled={isLoading}>
                            {isLoading ? "Loading..." : "Refresh"}
                        </button>
                    </div>

                    <form onSubmit={addTask} className="todo-form">
                        <input
                            type="text"
                            value={newTask}
                            onChange={(e) => setNewTask(e.target.value)}
                            placeholder="Add a new task to Google Tasks..."
                            className="todo-input"
                            disabled={isLoading}
                        />
                        <button type="submit" className="add-todo-btn" disabled={isLoading}>
                            Add
                        </button>
                    </form>

                    {error && <p className="error-message">{error}</p>}

                    {isLoading ? (
                        <div className="loading-indicator">
                            <p>Loading your tasks...</p>
                        </div>
                    ) : (
                        <ul className="todo-list">
                            {tasks.length === 0 ? (
                                <li className="empty-state">No tasks found. Add one above!</li>
                            ) : (
                                tasks.map(task => (
                                    <li key={task.id} className={`todo-item ${task.completed ? 'completed' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={task.completed}
                                            onChange={() => toggleCompleteTask(task.id)}
                                            id={`task-${task.id}`}
                                        />
                                        <label htmlFor={`task-${task.id}`}>
                                            <span className="checkbox-custom"></span>
                                            {task.title}
                                        </label>
                                    </li>
                                ))
                            )}
                        </ul>
                    )}

                    <div className="google-integration-footer">
                        {/* <img src="/google-tasks-logo.png" alt="Google Tasks" className="google-logo" /> */}
                        <span>Synced with Google Tasks</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;